// ============================================================================
// EmailBlastModal — admin composer for emailing event registrants (UIF only).
// ============================================================================
// Subject + markdown body + recipient filter + require_confirmation toggle.
// On submit, hits POST /api/admin/events/:id/email and renders a result card
// with sent / failed / failures.
//
// Placeholders shown in helper text (resolved server-side per recipient):
//   {first_name}, {event_title}, {event_date}, {confirm_link}
// If body doesn't contain {confirm_link} and require_confirmation is true, the
// backend appends a styled "Confirm my attendance" button to the email.
// ============================================================================

import { useState } from 'react';
import { Loader2, Send, AlertCircle, CheckCircle2, Eye } from 'lucide-react';
import { Modal } from './Modal';
import { api, type EmailBlastResult, type EmailRecipientFilter } from '../api';

interface Props {
  open: boolean;
  onClose: () => void;
  token: string;
  calendarEntryId: number;
  eventTitle: string;
  // Pre-counted registrant totals so the admin can see what they're about
  // to send to without round-tripping. Optional — if absent we'll display
  // generic copy and let the backend tell us at send time.
  counts?: {
    registered: number;
    waitlisted: number;
    registered_unconfirmed?: number;
    registered_confirmed?: number;
  };
  // Task #1: when provided, the modal opens in "selected attendees" mode
  // and the filter dropdown is hidden. Used by "Email selected (N)" from
  // the Registrations panel.
  selectedAttendeeIds?: number[];
  // Human-readable hint shown above the form when selectedAttendeeIds is
  // set, e.g. "12 attendees selected from the Registrations panel".
  selectionLabel?: string;
}

type Filter = EmailRecipientFilter;

const DEFAULT_BODY = `Hi {first_name},

Just a quick reminder about **{event_title}** on **{event_date}**. We're looking forward to seeing you there!

Please click the link below to confirm you'll be attending. This helps us plan materials, catering, and seating.

Let us know if anything changes by replying to this email.`;

export function EmailBlastModal({ open, onClose, token, calendarEntryId, eventTitle, counts, selectedAttendeeIds, selectionLabel }: Props) {
  const [subject, setSubject] = useState(`Quick check-in about ${eventTitle}`);
  const [bodyMd, setBodyMd] = useState(DEFAULT_BODY);
  const [filter, setFilter] = useState<Filter>('registered');
  const [requireConfirmation, setRequireConfirmation] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<EmailBlastResult | null>(null);

  // Selection-mode short-circuits the filter dropdown entirely.
  const inSelectionMode = !!(selectedAttendeeIds && selectedAttendeeIds.length > 0);

  const recipientCount =
    inSelectionMode ? selectedAttendeeIds!.length :
    counts == null ? null :
    filter === 'registered' ? counts.registered :
    filter === 'registered_unconfirmed' ? (counts.registered_unconfirmed ?? 0) :
    filter === 'registered_confirmed' ? (counts.registered_confirmed ?? 0) :
    filter === 'registered_and_waitlisted' ? counts.registered + counts.waitlisted :
    counts.registered + counts.waitlisted; // 'all_active'

  const handleSend = async () => {
    if (!subject.trim() || !bodyMd.trim()) {
      setError('Subject and body are required.'); return;
    }
    if (recipientCount === 0) {
      setError('No registrants match this filter.'); return;
    }
    const ok = window.confirm(
      `Send this email to ${recipientCount ?? '(all)'} recipient${recipientCount === 1 ? '' : 's'}?` +
      (requireConfirmation ? ' Each recipient will get a unique confirmation link.' : ''),
    );
    if (!ok) return;
    setBusy(true); setError(''); setResult(null);
    try {
      const r = await api.sendEventEmailBlast(token, calendarEntryId, {
        subject: subject.trim(),
        body_md: bodyMd.trim(),
        recipient_filter: filter,
        require_confirmation: requireConfirmation,
        attendee_ids: inSelectionMode ? selectedAttendeeIds : undefined,
      });
      setResult(r);
    } catch (err: any) {
      setError(err?.message || 'Could not send email blast.');
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    // Reset transient state but keep the composed message in case admin
    // wants to send another batch.
    setResult(null);
    setError('');
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title={`Email registrants — ${eventTitle}`} maxWidth="max-w-3xl">
      {result ? (
        <ResultCard result={result} onClose={handleClose} onSendAnother={() => setResult(null)} />
      ) : (
        <div className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
          )}

          {inSelectionMode ? (
            <div className="bg-uif-green/5 border border-uif-green/30 rounded-md px-3 py-2 text-sm text-uif-green-dark">
              <strong>Sending to {selectedAttendeeIds!.length} selected attendee{selectedAttendeeIds!.length === 1 ? '' : 's'}.</strong>
              {selectionLabel && <span className="ml-1 text-xs text-gray-600">{selectionLabel}</span>}
              <p className="text-[11px] text-gray-600 mt-0.5">
                Recipient filter is ignored — close and re-open from the panel to change the selection.
              </p>
            </div>
          ) : (
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Recipient filter</span>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as Filter)}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              >
                <option value="registered">Registered (all){counts != null ? ` — ${counts.registered}` : ''}</option>
                <option value="registered_unconfirmed">Registered — not yet confirmed{counts?.registered_unconfirmed != null ? ` — ${counts.registered_unconfirmed}` : ''}</option>
                <option value="registered_confirmed">Registered — already confirmed{counts?.registered_confirmed != null ? ` — ${counts.registered_confirmed}` : ''}</option>
                <option value="registered_and_waitlisted">Registered + waitlisted{counts != null ? ` — ${counts.registered + counts.waitlisted}` : ''}</option>
                <option value="all_active">All active (registered + waitlisted + attended)</option>
              </select>
            </label>
          )}

          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Subject *</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            />
          </label>

          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Body (markdown) *</span>
            <textarea
              rows={12}
              value={bodyMd}
              onChange={(e) => setBodyMd(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Placeholders: <code className="bg-gray-100 px-1 rounded">{'{first_name}'}</code>,{' '}
              <code className="bg-gray-100 px-1 rounded">{'{event_title}'}</code>,{' '}
              <code className="bg-gray-100 px-1 rounded">{'{event_date}'}</code>,{' '}
              <code className="bg-gray-100 px-1 rounded">{'{confirm_link}'}</code>.
              Markdown: <strong>**bold**</strong>, <em>*italic*</em>, <code>`code`</code>, [link](url), headings, lists.
            </p>
          </label>

          <label className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded-md p-3">
            <input
              type="checkbox"
              checked={requireConfirmation}
              onChange={(e) => setRequireConfirmation(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-uif-green"
            />
            <div>
              <div className="font-semibold text-amber-900">Require confirmation</div>
              <div className="text-xs text-amber-800 mt-0.5">
                {requireConfirmation
                  ? `Each recipient gets a unique link. The email will auto-include a "Confirm my attendance" button if you don't paste {confirm_link} into the body. Click-throughs flip their status to confirmed.`
                  : 'No confirmation link will be included or auto-appended. Use this for general updates that don\'t need a click-through response.'}
              </div>
            </div>
          </label>

          <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-gray-200">
            <div className="text-xs text-gray-600">
              {recipientCount != null && (
                <>About to send to <span className="font-semibold text-uif-green-dark">{recipientCount}</span> recipient{recipientCount === 1 ? '' : 's'}</>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowPreview((v) => !v)}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Eye className="h-4 w-4" /> {showPreview ? 'Hide preview' : 'Preview'}
              </button>
              <button
                type="button"
                onClick={handleClose}
                disabled={busy}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={busy || !subject.trim() || !bodyMd.trim()}
                className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-4 py-1.5 text-sm hover:bg-uif-green-dark disabled:bg-gray-300"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send email
              </button>
            </div>
          </div>

          {showPreview && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-2">
              <div className="text-[11px] text-gray-500 italic">
                Preview shows the message with sample placeholder values. Each recipient gets a personalized version.
              </div>
              <div className="bg-white border border-gray-200 rounded-md p-4">
                <div className="text-xs text-gray-500">Subject:</div>
                <div className="font-semibold text-gray-900 mb-3">
                  {previewReplace(subject, eventTitle)}
                </div>
                <div className="text-xs text-gray-500">Body:</div>
                <div className="prose-content text-sm whitespace-pre-wrap">
                  {previewReplace(bodyMd, eventTitle)}
                </div>
                {requireConfirmation && (
                  <div className="text-center mt-4">
                    <span className="inline-block bg-uif-green text-white font-semibold px-4 py-2 rounded">
                      Confirm my attendance
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function previewReplace(s: string, eventTitle: string): string {
  return s
    .replace(/\{first_name\}/g, 'Jane')
    .replace(/\{event_title\}/g, eventTitle)
    .replace(/\{event_date\}/g, 'July 13, 2026')
    .replace(/\{confirm_link\}/g, 'https://events.upinfarms.com/#confirm/[unique-token]');
}

function ResultCard({ result, onClose, onSendAnother }: {
  result: EmailBlastResult;
  onClose: () => void;
  onSendAnother: () => void;
}) {
  const allOk = result.failed === 0;
  return (
    <div className="space-y-4">
      <div className={
        'rounded-lg p-4 flex items-start gap-3 ' +
        (allOk ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200')
      }>
        {allOk
          ? <CheckCircle2 className="h-6 w-6 text-green-700 mt-0.5 flex-shrink-0" />
          : <AlertCircle className="h-6 w-6 text-amber-700 mt-0.5 flex-shrink-0" />
        }
        <div className="flex-1">
          <div className={'font-semibold ' + (allOk ? 'text-green-900' : 'text-amber-900')}>
            Sent {result.sent} of {result.total}{result.failed > 0 ? ` · ${result.failed} failed` : ''}
          </div>
          <p className={'text-xs mt-0.5 ' + (allOk ? 'text-green-800' : 'text-amber-800')}>
            {allOk
              ? 'All emails delivered to SES. Watch the Confirmed column on the Registrations panel to see who clicks through.'
              : 'Some deliveries failed — see the list below. The remaining recipients got their messages.'}
          </p>
        </div>
      </div>
      {result.failures.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-md max-h-48 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Failed recipient</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {result.failures.map((f, i) => (
                <tr key={i}>
                  <td className="px-3 py-2 font-mono">{f.to}</td>
                  <td className="px-3 py-2 text-red-700">{f.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
        <button
          type="button"
          onClick={onSendAnother}
          className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          Send another
        </button>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-sm hover:bg-uif-green-dark"
        >
          Done
        </button>
      </div>
    </div>
  );
}
